import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import {
  getMyLscMemberClaimSuggestions,
  submitLscMemberClaim,
  LscApiError,
} from '../api';
import type { LscMemberClaimSuggestion } from '../types';
import ErrorBanner from './ErrorBanner';
import { lscPath } from '../routing';

interface MemberClaimSuggestionPanelProps {
  leagueId: string;
  onClaimSubmitted?: () => void;
}

const MemberClaimSuggestionPanel: React.FC<MemberClaimSuggestionPanelProps> = ({
  leagueId,
  onClaimSubmitted,
}) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [discordConnected, setDiscordConnected] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<LscMemberClaimSuggestion[]>([]);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyLscMemberClaimSuggestions(leagueId);
      setDiscordConnected(Boolean(data.discord_connected));
      setMessage(data.message || null);
      setSuggestions(data.suggestions || []);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load claim suggestions.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions, user?.discord_connected]);

  const handleRequestClaim = async (suggestion: LscMemberClaimSuggestion) => {
    if (!suggestion.member_id) return;
    setSubmittingId(suggestion.member_id);
    setError(null);
    try {
      await submitLscMemberClaim(leagueId, suggestion.member_id);
      await loadSuggestions();
      onClaimSubmitted?.();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not submit claim.');
    } finally {
      setSubmittingId(null);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="member-claim-suggestion-panel">
        <p className="text-sm text-secondary-400">Checking for imported league profiles...</p>
      </div>
    );
  }

  if (!discordConnected) {
    return (
      <div className="card space-y-3" data-testid="member-claim-suggestion-panel">
        <h2 className="text-lg font-semibold text-white">Claim your league profile</h2>
        <p className="text-sm text-secondary-400" data-testid="connect-discord-prompt">
          Connect Discord to claim your NeonSportz league profile.
        </p>
        <Link
          to={lscPath(`/leagues/${leagueId}/settings`)}
          className="btn-secondary inline-block"
          data-testid="connect-discord-link"
        >
          Connect Discord in Settings
        </Link>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="card" data-testid="member-claim-suggestion-panel">
        <p className="text-sm text-secondary-400">
          {message || 'No imported profile matches your Discord account.'}
        </p>
      </div>
    );
  }

  return (
    <div className="card space-y-4" data-testid="member-claim-suggestion-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">We found your imported league profile</h2>
        <p className="text-sm text-secondary-400">
          Request commissioner approval to link your Stream Center account to your imported roster
          slot.
        </p>
      </div>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {suggestions.map((suggestion) => {
        const pending = suggestion.pending_claim?.status === 'pending';
        return (
          <div
            key={suggestion.member_id}
            className="rounded-lg border border-secondary-700 p-4 space-y-2"
            data-testid="claim-suggestion-card"
          >
            <p className="text-white font-medium">{suggestion.side_label || 'Imported team'}</p>
            <p className="text-sm text-secondary-300">
              Discord: {suggestion.discord_username || suggestion.discord_id || '—'}
            </p>
            <p className="text-sm text-secondary-300">
              In-game: {suggestion.in_game_name || '—'}
            </p>
            {pending ? (
              <span
                className="inline-block text-xs uppercase tracking-wide text-amber-300"
                data-testid="pending-claim-status"
              >
                Claim pending commissioner review
              </span>
            ) : (
              <button
                type="button"
                className="btn-primary"
                disabled={submittingId === suggestion.member_id}
                onClick={() => void handleRequestClaim(suggestion)}
                data-testid="request-claim-button"
              >
                {submittingId === suggestion.member_id ? 'Submitting...' : 'Request claim'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MemberClaimSuggestionPanel;
